import locale from "@bonliva-crm/locale";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  ConsultantAssignTagsActionFormValues,
  consultantAssignTagsActionFormSchema
} from "../components/actions/action-form-values";
import { AssignTagsAction } from "../components/actions/assign-tags";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionHookReturn } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { useBulkActions } from "./use-bulk-actions";

type Props<T extends SelectionStepRowType> = {
  valueParser: ExternalActionStepProps<T>["valueParsers"]["ConsultantAssignTags"];
  selectedRows: T[];
  totalRows: number;
  entitySingular: string;
  entityPlural: string;
};

export const useConsultantAssignTagsAction = <T extends SelectionStepRowType>(
  props: Props<T>
): BulkActionHookReturn => {
  const classes = useStyles();
  const { valueParser, selectedRows, totalRows, entitySingular, entityPlural } = props;

  const parsedValues = useMemo(
    () => valueParser(selectedRows),
    [valueParser, selectedRows]
  );

  const consultantAssignTagsActionForm = useForm<ConsultantAssignTagsActionFormValues>({
    defaultValues: {
      tags: []
    },
    mode: "onChange",
    resolver: yupResolver(consultantAssignTagsActionFormSchema)
  });

  const { assignConsultantTags } = useBulkActions({});

  const actionContent = <AssignTagsAction form={consultantAssignTagsActionForm} />;

  const confirmationContent = (
    <Box className={classes.confirmationContent}>
      <Typography variant="body1">
        {locale.assignTagsConfirmation(
          consultantAssignTagsActionForm.getValues("tags").length,
          totalRows,
          entitySingular,
          entityPlural
        )}
      </Typography>
      <Typography variant="body1">{locale.youWillBeNotifiedViaEmail}</Typography>
    </Box>
  );

  const onSubmit = consultantAssignTagsActionForm.handleSubmit((data) =>
    assignConsultantTags({
      tags: data.tags,
      consultantIds: parsedValues.nodeIds
    })
  );

  const reset = () => {
    consultantAssignTagsActionForm.reset();
  };

  const nextStepDisabled = !consultantAssignTagsActionForm.formState.isValid;

  return {
    actionContent,
    confirmationContent,
    onSubmit,
    reset,
    nextStepDisabled
  };
};

const useStyles = makeStyles((theme) => ({
  confirmationContent: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }
}));
